import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, LinearProgress, Chip, Button, CircularProgress,
  Alert, Stack, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlaceIcon from '@mui/icons-material/Place';
import apiClient from '../services/apiClient';

/**
 * BreadcrumbsTrail — the visual "trail" for a path (roadmap #2).
 *
 * Renders an ordered spine of story nodes with clear states
 * (complete / in_progress / available / not started), a "you are here" marker,
 * percent-complete, badge milestones, and a primary "Continue" CTA.
 *
 * Ordering is SOFT: the backend's `locked` state only means "an earlier chapter
 * isn't finished yet". Those chapters stay clickable and read "Not started" —
 * adults skip ahead and re-read. Consecutive chapters that share a `section`
 * are grouped under a part heading.
 *
 * Self-contained: fetches GET /breadcrumbs/paths/:id/trail. Calls
 * onSelectStory(storyId) when a reachable node (or Continue) is activated.
 *
 * Props:
 *   pathId        (string, required)
 *   languageCode  (string, required)
 *   onSelectStory (fn(storyId))  navigation callback
 */

const BADGE_META = {
  started: { label: 'Started', color: 'info' },
  halfway: { label: 'Halfway', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
};

// Visual treatment per node state, resolved against the MUI theme palette.
function nodeVisual(theme, state) {
  switch (state) {
    case 'complete':
      return { ring: theme.palette.success.main, fill: theme.palette.success.light,
               fg: theme.palette.success.main, clickable: true };
    case 'in_progress':
      return { ring: theme.palette.warning.main, fill: theme.palette.warning.light,
               fg: theme.palette.warning.main, clickable: true };
    case 'available':
      return { ring: theme.palette.primary.main, fill: theme.palette.background.paper,
               fg: theme.palette.primary.main, clickable: true };
    case 'locked':  // soft order: not started yet, still openable
    default:
      return { ring: theme.palette.grey[400], fill: theme.palette.background.paper,
               fg: theme.palette.text.secondary, clickable: true };
  }
}

function NodeIcon({ state }) {
  if (state === 'complete') return <CheckCircleIcon />;
  if (state === 'in_progress') return <PlayCircleIcon />;
  return <RadioButtonUncheckedIcon />;
}

export default function BreadcrumbsTrail({ pathId, languageCode, onSelectStory }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchTrail = useCallback(async () => {
    if (!pathId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.breadcrumbs.getTrail(pathId, languageCode);
      setData(res.data);
    } catch (e) {
      console.error('Failed to load trail', e);
      setError('Could not load this path right now.');
    } finally {
      setLoading(false);
    }
  }, [pathId, languageCode]);

  useEffect(() => { fetchTrail(); }, [fetchTrail]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data || !data.nodes || data.nodes.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        <Typography variant="body1">No stories in this path yet.</Typography>
      </Box>
    );
  }

  const { nodes, progress = {}, current_story_id, badge_milestones = {} } = data;
  const pct = progress.percentage || 0;
  const handleSelect = (node) => {
    if (!node) return;
    if (onSelectStory) onSelectStory(node.story_id);
  };
  const currentNode = nodes.find((n) => n.story_id === current_story_id);

  return (
    <Box>
      {/* Progress header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {pct}% Complete
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {progress.completed || 0} of {progress.total || 0} stories
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 8, borderRadius: 4, bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            },
          }}
        />

        {/* Badge milestones */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          {Object.keys(BADGE_META).map((key) => {
            const earned = !!badge_milestones[key];
            const meta = BADGE_META[key];
            return (
              <Chip
                key={key}
                size="small"
                icon={<EmojiEventsIcon />}
                label={meta.label}
                color={earned ? meta.color : 'default'}
                variant={earned ? 'filled' : 'outlined'}
                sx={{ opacity: earned ? 1 : 0.55 }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* Continue CTA */}
      {currentNode && (
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<PlayCircleIcon />}
          onClick={() => handleSelect(currentNode)}
          sx={{ mb: 3, py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {progress.completed > 0 ? 'Continue' : 'Start'}: {currentNode.title}
        </Button>
      )}

      {/* The trail */}
      <Box sx={{ position: 'relative' }}>
        {nodes.map((node, i) => {
          const v = nodeVisual(theme, node.state);
          const isLast = i === nodes.length - 1;
          const startsSection = !!node.section && node.section !== nodes[i - 1]?.section;
          return (
            <React.Fragment key={node.story_id}>
            {startsSection && (
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', fontWeight: 700, letterSpacing: 1, mt: i === 0 ? 0 : 1, mb: 1.5 }}
              >
                {node.section}
              </Typography>
            )}
            <Box
              onClick={() => handleSelect(node)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                pb: isLast ? 0 : 3,
                cursor: v.clickable ? 'pointer' : 'default',
                '&:hover .trail-card': v.clickable
                  ? { borderColor: 'primary.main', boxShadow: 2 }
                  : {},
              }}
            >
              {/* Connector line (behind the node circle) */}
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 19, top: 40, bottom: 0, width: 3, borderRadius: 2,
                    bgcolor: node.state === 'complete' ? 'success.main' : 'grey.300',
                  }}
                />
              )}

              {/* Node circle — current node gets a gold ring + halo */}
              <Box
                sx={{
                  zIndex: 1, flexShrink: 0,
                  width: 40, height: 40, borderRadius: '50%',
                  bgcolor: node.is_current ? theme.palette.secondary.light : v.fill,
                  border: `${node.is_current ? 3 : 2}px solid ${node.is_current ? theme.palette.secondary.main : v.ring}`,
                  color: node.is_current ? theme.palette.secondary.dark : v.fg,
                  boxShadow: node.is_current ? '0 0 0 4px rgba(212,175,55,0.25)' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                {node.state === 'available' || node.state === 'locked'
                  ? <Typography variant="body2" sx={{ fontWeight: 700, color: node.is_current ? 'secondary.dark' : v.fg }}>{i + 1}</Typography>
                  : <NodeIcon state={node.state} />}
              </Box>

              {/* Node card */}
              <Box
                className="trail-card"
                sx={{
                  flexGrow: 1,
                  border: '1px solid',
                  borderColor: node.is_current ? 'secondary.main' : 'divider',
                  borderRadius: 2,
                  px: 2, py: 1.25,
                  bgcolor: node.is_current ? 'rgba(212,175,55,0.10)' : 'background.paper',
                  transition: 'border-color .15s, box-shadow .15s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {node.title}
                  </Typography>
                  {node.is_current && (
                    <Chip
                      size="small"
                      color="secondary"
                      icon={<PlaceIcon />}
                      label="You are here"
                      sx={{ height: 22, fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                  {node.state === 'complete' && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      Completed
                    </Typography>
                  )}
                  {node.state === 'in_progress' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                      In progress
                    </Typography>
                  )}
                  {node.state === 'locked' && (
                    <Typography variant="caption" color="text.secondary">
                      Not started
                    </Typography>
                  )}
                  {typeof node.difficulty_rating === 'number' && (
                    <Typography variant="caption" color="text.secondary">
                      Difficulty {node.difficulty_rating}/5
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
